import axiosInstance from './AxiosInstance';

/**
 * Obtiene todas las vacantes de la empresa autenticada
 * GET /vacantes
 */
export function getVacancies() {
    return axiosInstance.get('/vacantes');
}

/**
 * Obtiene los detalles de una vacante específica
 * GET /vacantes/{id}
 * @param {string} id - ID de la vacante (UUID)
 */
export function getVacancyById(id) {
    return axiosInstance.get(`/vacantes/${id}`);
}

/**
 * Crea una nueva vacante
 * POST /vacantes
 * @param {Object} vacancyData - Datos de la vacante a crear
 */
export function createVacancy(vacancyData) {
    return axiosInstance.post('/vacantes', vacancyData);
}

/**
 * Actualiza una vacante completa
 * PUT /vacantes/{id}
 * @param {string} id - ID de la vacante (UUID)
 * @param {Object} vacancyData - Datos completos de la vacante
 */
export function updateVacancy(id, vacancyData) {
    return axiosInstance.put(`/vacantes/${id}`, vacancyData);
}

/**
 * Actualiza campos específicos de una vacante
 * PATCH /vacantes/{id}
 * @param {string} id - ID de la vacante (UUID)
 * @param {Object} vacancyData - Datos parciales de la vacante a actualizar
 */
export function patchVacancy(id, vacancyData) {
    return axiosInstance.patch(`/vacantes/${id}`, vacancyData);
}

/**
 * Elimina una vacante (soft delete)
 * DELETE /vacantes/{id}
 * @param {string} id - ID de la vacante (UUID)
 */
export function deleteVacancy(id) {
    return axiosInstance.delete(`/vacantes/${id}`);
}

/**
 * Alterna el estado activo/inactivo de una vacante
 * PATCH /vacantes/{id}/toggle-activa
 * @param {string} id - ID de la vacante (UUID)
 */
export function toggleVacancyActive(id) {
    return axiosInstance.patch(`/vacantes/${id}/toggle-activa`);
}

/**
 * Obtiene vacantes públicas con filtros y paginación (sin autenticación)
 * GET /vacantes/public
 * @param {Object} params - Parámetros de búsqueda y filtros
 * @param {string} params.query - Búsqueda de texto libre
 * @param {string} params.categoriaId - ID de la categoría
 * @param {string} params.modalidadTrabajoId - ID de la modalidad de trabajo
 * @param {string} params.tipoTrabajoId - ID del tipo de trabajo
 * @param {string} params.tipoContratoTrabajoId - ID del tipo de contrato
 * @param {string} params.tiempoExperienciaRequeridoId - ID del tiempo de experiencia
 * @param {string} params.paisId - ID del país
 * @param {string} params.ciudadId - ID de la ciudad
 * @param {number} params.salarioMinimo - Salario mínimo
 * @param {number} params.salarioMaximo - Salario máximo
 * @param {boolean} params.inglesRequerido - Si se requiere inglés
 * @param {string} params.tags - Tags separados por comas
 * @param {number} params.page - Número de página
 * @param {number} params.limit - Límite de resultados por página
 * @param {string} params.orderBy - Campo por el cual ordenar
 * @param {string} params.orderDirection - Dirección del ordenamiento (asc/desc)
 */
export function getPublicVacancies(params = {}) {
    const queryParams = new URLSearchParams();

    // Agregar solo los parámetros que tienen valor
    if (params.query) queryParams.append('query', params.query);
    if (params.categoriaId) queryParams.append('categoriaId', params.categoriaId);
    if (params.modalidadTrabajoId) queryParams.append('modalidadTrabajoId', params.modalidadTrabajoId);
    if (params.tipoTrabajoId) queryParams.append('tipoTrabajoId', params.tipoTrabajoId);
    if (params.tipoContratoTrabajoId) queryParams.append('tipoContratoTrabajoId', params.tipoContratoTrabajoId);
    if (params.tiempoExperienciaRequeridoId) queryParams.append('tiempoExperienciaRequeridoId', params.tiempoExperienciaRequeridoId);
    if (params.paisId) queryParams.append('paisId', params.paisId);
    if (params.ciudadId) queryParams.append('ciudadId', params.ciudadId);
    if (params.salarioMinimo !== undefined && params.salarioMinimo !== null && params.salarioMinimo !== '') {
        queryParams.append('salarioMinimo', params.salarioMinimo);
    }
    if (params.salarioMaximo !== undefined && params.salarioMaximo !== null && params.salarioMaximo !== '') {
        queryParams.append('salarioMaximo', params.salarioMaximo);
    }
    if (params.inglesRequerido !== undefined && params.inglesRequerido !== null) {
        queryParams.append('inglesRequerido', params.inglesRequerido);
    }
    if (params.tags) queryParams.append('tags', params.tags);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.orderBy) queryParams.append('orderBy', params.orderBy);
    if (params.orderDirection) queryParams.append('orderDirection', params.orderDirection);

    const queryString = queryParams.toString();
    const url = `/vacantes/public${queryString ? `?${queryString}` : ''}`;

    return axiosInstance.get(url);
}

/**
 * Obtiene los detalles de una vacante pública específica (sin autenticación)
 * GET /vacantes/public/{id}
 * @param {string} id - ID de la vacante (UUID)
 */
export function getPublicVacancyById(id) {
    return axiosInstance.get(`/vacantes/public/${id}`);
}

/**
 * Postula a una vacante (crear una postulación)
 * POST /postulaciones
 * @param {string} vacanteId - ID de la vacante (UUID)
 * @param {string} estado - Estado inicial de la postulación (ej: 'Borrador')
 */
export function applyToVacancy(vacanteId, estado = 'Borrador') {
    const payload = {
        vacanteId,
        estado,
    };

    return axiosInstance.post('/job-applications', payload);
}

/**
 * Actualiza el estado de una postulación (job-application)
 * PATCH /job-applications/{id}
 * @param {string} applicationId - ID de la postulación
 * @param {string} estado - Nuevo estado (ej: 'Enviado')
 */
export function updateJobApplicationStatus(applicationId, estado) {
    return axiosInstance.patch(`/job-applications/${applicationId}`, { estado });
}

/**
 * Actualiza el estado de una postulación por parte del empleador
 * PATCH /job-applications/{id}/estado-empresa
 * @param {string} applicationId - ID de la postulación
 * @param {string} estado - Nuevo estado: 'Aplicado', 'En Revision', 'Preseleccionado', 'Aceptado', 'Rechazado'
 */
export function updateApplicationStatusByEmployer(applicationId, estado) {
    return axiosInstance.patch(`/job-applications/${applicationId}/estado-empresa`, { estado });
}

/**
 * Obtiene las vacantes a las que el usuario ha aplicado
 * GET /job-applications?page=1&limit=10
 * @param {Object} params - Parámetros de paginación
 * @returns {Promise} Lista de postulaciones con paginación
 */
export function getAppliedJobs(params = {}) {
    return axiosInstance.get('/job-applications', { params });
}

/**
 * Obtiene los postulantes de una vacante específica (para empresas)
 * GET /vacantes/{vacanteId}/applicants?page=1&limit=10&estado=
 * @param {string} vacanteId - ID de la vacante
 * @param {number} page - Número de página
 * @param {number} limit - Límite de resultados por página
 * @param {string} estado - Filtro de estado (opcional)
 * @returns {Promise} Lista de postulaciones para la vacante con paginación
 */
export function getApplicationsByVacancy(vacanteId, page = 1, limit = 10, estado = '') {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    if (estado) {
        params.append('estado', estado);
    }
    return axiosInstance.get(`/vacantes/${vacanteId}/applicants?${params.toString()}`);
}
